/**
 * Step 1: URDF Input
 * Allows users to upload, paste, or import URDF from builder
 */

import { useState, useCallback } from "react";
import {
  Box,
  Title,
  Text,
  Tabs,
  Textarea,
  Button,
  Group,
  Stack,
  Paper,
  Alert,
  FileInput,
  Code,
  LoadingOverlay,
} from "@mantine/core";
import {
  IconUpload,
  IconClipboard,
  IconAlertCircle,
  IconCheck,
} from "@tabler/icons-react";
import { useConfigureStore } from "../ConfigureStore";
import { toJointInfo, toLinkInfo } from "../urdfWire";

interface UrdfInputStepProps {
  onNext: () => void;
}

export function UrdfInputStep({ onNext }: UrdfInputStepProps) {
  const [activeTab, setActiveTab] = useState<string | null>("upload");
  const [pastedContent, setPastedContent] = useState("");
  const [isParsing, setIsParsing] = useState(false);

  const {
    urdfFileName,
    isUrdfParsed,
    parseError,
    joints,
    links,
    setUrdfContent,
    setUrdfParsed,
    setParseError,
  } = useConfigureStore();

  const parseUrdf = useCallback(async (content: string, fileName?: string) => {
    setIsParsing(true);
    setUrdfContent(content, fileName);

    try {
      const response = await fetch("/api/configure/parse-urdf", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ urdf_content: content }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to parse URDF");
      }

      const data = await response.json();

      setUrdfParsed(
        data.joints.map(toJointInfo),
        data.links.map(toLinkInfo),
        data.root_link
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error occurred";
      setParseError(message);
    } finally {
      setIsParsing(false);
    }
  }, [setUrdfContent, setUrdfParsed, setParseError]);

  const handleFileChange = useCallback(
    (file: File | null) => {
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const content = e.target?.result;
          if (typeof content === "string") {
            parseUrdf(content, file.name);
          }
        };
        reader.readAsText(file);
      }
    },
    [parseUrdf]
  );

  const handlePasteSubmit = useCallback(() => {
    if (pastedContent.trim()) {
      parseUrdf(pastedContent.trim());
    }
  }, [pastedContent, parseUrdf]);

  return (
    <Box pos="relative">
      <LoadingOverlay visible={isParsing} zIndex={1000} />

      <Stack gap="lg">
        <Box>
          <Title order={3} mb="xs">
            Input URDF
          </Title>
          <Text c="dimmed" size="sm">
            Upload a URDF file, paste URDF content, or import from a builder session.
          </Text>
        </Box>

        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="upload" leftSection={<IconUpload size={16} />}>
              Upload File
            </Tabs.Tab>
            <Tabs.Tab value="paste" leftSection={<IconClipboard size={16} />}>
              Paste Content
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="upload" pt="md">
            <Stack gap="md">
              <FileInput
                label="URDF File"
                placeholder="Click to select a URDF file"
                accept=".urdf,.xml"
                onChange={handleFileChange}
                leftSection={<IconUpload size={16} />}
              />

              <Text size="sm" c="dimmed">
                Supports .urdf and .xml files. Drag and drop is also supported.
              </Text>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="paste" pt="md">
            <Stack gap="md">
              <Textarea
                label="URDF Content"
                placeholder="Paste your URDF XML content here..."
                minRows={10}
                maxRows={20}
                value={pastedContent}
                onChange={(e) => setPastedContent(e.currentTarget.value)}
                styles={{
                  input: {
                    fontFamily: "monospace",
                    fontSize: "12px",
                  },
                }}
              />
              <Button onClick={handlePasteSubmit} disabled={!pastedContent.trim()}>
                Parse URDF
              </Button>
            </Stack>
          </Tabs.Panel>
        </Tabs>

        {/* Parse Error */}
        {parseError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Parse Error"
            color="red"
            variant="light"
          >
            {parseError}
          </Alert>
        )}

        {/* Parse Success */}
        {isUrdfParsed && (
          <Alert
            icon={<IconCheck size={16} />}
            title="URDF Parsed Successfully"
            color="green"
            variant="light"
          >
            <Stack gap="xs">
              {urdfFileName && (
                <Text size="sm">
                  <strong>File:</strong> {urdfFileName}
                </Text>
              )}
              <Text size="sm">
                <strong>Joints:</strong> {joints.length} (
                {joints.filter((j) => j.type !== "fixed").length} actuated)
              </Text>
              <Text size="sm">
                <strong>Links:</strong> {links.length}
              </Text>
            </Stack>
          </Alert>
        )}

        {/* Preview */}
        {isUrdfParsed && joints.length > 0 && (
          <Paper withBorder p="md">
            <Title order={5} mb="sm">
              Detected Joints
            </Title>
            <Stack gap="xs">
              {joints.slice(0, 10).map((joint) => (
                <Group key={joint.name} justify="space-between">
                  <Code>{joint.name}</Code>
                  <Text size="sm" c="dimmed">
                    {joint.type}
                  </Text>
                </Group>
              ))}
              {joints.length > 10 && (
                <Text size="sm" c="dimmed">
                  ... and {joints.length - 10} more joints
                </Text>
              )}
            </Stack>
          </Paper>
        )}

        {/* Auto-proceed when parsed */}
        {isUrdfParsed && (
          <Group justify="flex-end">
            <Button onClick={onNext}>
              Continue to Servo Mapping
            </Button>
          </Group>
        )}
      </Stack>
    </Box>
  );
}
